import { useMsal } from '@azure/msal-react'
import {
  BarChart3,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Search,
  Share2,
  SlidersHorizontal,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { PoweredByFooter } from '@/components/PoweredByFooter'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useBanner } from '@/hooks/useBanner'
import { trackEvent } from '@/lib/analytics'

// ─────────────────────────────────────────────────────────────
// STEP 1: TYPES
// Describes the shape of one record returned by the API
// ─────────────────────────────────────────────────────────────
interface MarketInsightRecord {
  Location: string
  Date: string
  MedianSalePrice: number
  MedianValuation: number
  MedianListPrice: number
  PercSaleToValDiff: number
  PercListPriceToValDiff: number
  NoOfSales: number
  PercOfNationalSales: number
  PercSalePriceDiffLastPeriod: number | null
  DaysToSell: number
  HousePriceIndex: number
}

// const nationalStats = {
//   medianPrice: 805000,
//   yoyChange: 1.8,
//   totalSales: 8420,
//   avgDaysToSell: 34,
//   clearanceRate: 67,
// };

// ─────────────────────────────────────────────────────────────
// STEP 2: HELPER FUNCTIONS
// Small reusable utilities used throughout the component
// ─────────────────────────────────────────────────────────────

// Format a number into a price string e.g. 1020000 → "$1.02M"
function formatPrice(price: number): string {
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(2)}M`
  return `$${(price / 1_000).toFixed(0)}K`
}

// Format a Date to "yyyy-MM-dd" — required by the API
function toApiDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Safe fallback max — 2 months ago, used while the real latest month is loading
function getDefaultLatestMonth(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() - 2, 1)
}

// Fetches the most recent month that has real data from the API
async function fetchLatestAvailableMonth(): Promise<Date> {
  const now = new Date()
  const params = new URLSearchParams({
    GroupingType: '1',
    Grouping1: '0',
    Grouping2: 'all',
    Frequency: '12',
    DateFrom: toApiDateString(new Date(now.getFullYear(), now.getMonth() - 3, 1)),
    DateTo: toApiDateString(now),
  })
  const res = await fetch(`api/report/market-insights?${params}`)
  if (!res.ok) throw new Error('Failed')
  const records: MarketInsightRecord[] = await res.json()
  const latest = records
    .filter((r) => r.MedianSalePrice > 0)
    .map((r) => new Date(r.Date))
    .sort((a, b) => b.getTime() - a.getTime())[0]
  if (!latest) throw new Error('No data')
  return new Date(latest.getFullYear(), latest.getMonth(), 1)
}

const SHORT_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function MonthPicker({
  value,
  latestMonth,
  onChange,
}: {
  value: Date
  latestMonth: Date
  onChange: (date: Date) => void
}) {
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(value.getFullYear())
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const minYear = latestMonth.getFullYear() - 10
  const maxYear = latestMonth.getFullYear()

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => {
          setOpen((o) => !o)
          setViewYear(value.getFullYear())
        }}
        className="flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm bg-background hover:bg-muted transition-colors"
      >
        {MONTH_NAMES[value.getMonth()]} {value.getFullYear()}
        <ChevronRight
          className={`w-3 h-3 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-1 bg-background border rounded-lg shadow-lg p-3 w-52">
          {/* Year navigation */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setViewYear((y) => y - 1)}
              disabled={viewYear <= minYear}
              className="p-1 rounded hover:bg-muted disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold">{viewYear}</span>
            <button
              onClick={() => setViewYear((y) => y + 1)}
              disabled={viewYear >= maxYear}
              className="p-1 rounded hover:bg-muted disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {/* Month grid */}
          <div className="grid grid-cols-3 gap-1">
            {SHORT_MONTH_NAMES.map((name, idx) => {
              const monthDate = new Date(viewYear, idx, 1)
              const isDisabled = monthDate > latestMonth
              const isSelected = value.getFullYear() === viewYear && value.getMonth() === idx
              return (
                <button
                  key={name}
                  onClick={() => {
                    if (!isDisabled) {
                      onChange(monthDate)
                      setOpen(false)
                    }
                  }}
                  disabled={isDisabled}
                  className={`py-1.5 text-sm rounded font-medium transition-colors ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : isDisabled
                        ? 'text-muted-foreground/30 cursor-not-allowed'
                        : 'hover:bg-muted text-foreground'
                  }`}
                >
                  {name}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// ─────────────────────────────────────────────────────────────
// SHARED PDF SHARE UTILITY
// ─────────────────────────────────────────────────────────────
async function shareInsightsPdf(
  contentEl: HTMLElement,
  title: string,
  fileName: string,
  partyId: string,
): Promise<string | null> {
  const allEls = [contentEl, ...Array.from(contentEl.querySelectorAll<HTMLElement>('*'))]
  const savedCssTexts = allEls.map((el) => el.style.cssText)
  allEls.forEach((el) => {
    const cs = window.getComputedStyle(el)
    el.style.color = cs.color
    const bg = cs.backgroundColor
    if (bg && bg !== 'rgba(0, 0, 0, 0)') el.style.backgroundColor = bg
    el.style.borderTopColor = cs.borderTopColor
    el.style.borderRightColor = cs.borderRightColor
    el.style.borderBottomColor = cs.borderBottomColor
    el.style.borderLeftColor = cs.borderLeftColor
    el.style.borderRadius = cs.borderRadius
  })
  // Measure range buttons before capture so we can redraw them in jsPDF
  const containerRect = contentEl.getBoundingClientRect()
  type RangeBtn = {
    label: string
    active: boolean
    top: number
    left: number
    width: number
    height: number
  }
  const rangeBtns: RangeBtn[] = []
  contentEl.querySelectorAll<HTMLElement>('[data-range-btn]').forEach((el) => {
    const br = el.getBoundingClientRect()
    rangeBtns.push({
      label: el.getAttribute('data-range-btn') || '',
      active: el.getAttribute('data-range-active') === 'true',
      top: br.top - containerRect.top,
      left: br.left - containerRect.left,
      width: br.width,
      height: br.height,
    })
    el.style.visibility = 'hidden'
  })
  contentEl.querySelectorAll<HTMLElement>('[data-share-btn]').forEach((el) => {
    el.style.visibility = 'hidden'
  })
  // Measure child positions BEFORE html2canvas so layout is unaffected
  const preContainerTop = contentEl.getBoundingClientRect().top
  const preCutPoints = Array.from(contentEl.children).map((child) => {
    return (child as HTMLElement).getBoundingClientRect().bottom - preContainerTop
  })
  // Measure any elements marked as forced page-break starts
  const preForceBreakTops: number[] = []
  contentEl.querySelectorAll<HTMLElement>('[data-force-page-break]').forEach((el) => {
    preForceBreakTops.push(el.getBoundingClientRect().top - preContainerTop)
  })
  try {
    const { jsPDF } = await import('jspdf')
    const html2canvas = (await import('html2canvas')).default
    const canvas = await html2canvas(contentEl, { scale: 3, useCORS: true, logging: false })
    allEls.forEach((el, i) => {
      el.style.cssText = savedCssTexts[i]
    })
    contentEl.querySelectorAll<HTMLElement>('[data-range-btn]').forEach((el) => {
      el.style.visibility = ''
    })
    contentEl.querySelectorAll<HTMLElement>('[data-share-btn]').forEach((el) => {
      el.style.visibility = ''
    })
    const pageWidth = 210
    const pageHeight = 297
    const margin = 20
    const imgWidth = pageWidth - margin * 2
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' })
    const fetchImgDataUrl = async (url: string): Promise<string | null> => {
      try {
        const resp = await fetch(url)
        if (!resp.ok) return null
        const blob = await resp.blob()
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        })
      } catch {
        return null
      }
    }

    const makeCircularDataUrl = (dataUrl: string, size: number, offsetY = 50): Promise<string> =>
      new Promise((resolve, reject) => {
        const img = new Image()
        img.onerror = reject
        img.onload = () => {
          const c = document.createElement('canvas')
          c.width = size
          c.height = size
          const ctx = c.getContext('2d')!
          ctx.beginPath()
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
          ctx.closePath()
          ctx.clip()
          const s = Math.min(img.width, img.height)
          const sx = (img.width - s) / 2
          const sy = (img.height - s) * (offsetY / 100)
          ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size)
          resolve(c.toDataURL('image/png'))
        }
        img.src = dataUrl
      })

    let brandDataUrl: string | null = null
    let headshotDataUrl: string | null = null
    let pdfTagline = ''
    let agentName = ''
    let agentContact = ''
    let agentOrg = ''
    if (partyId) {
      const [brand, headshot, taglineRes, offsetRes, profileRes] = await Promise.all([
        fetchImgDataUrl(`/api/userimage/brand/data?userId=${encodeURIComponent(partyId)}`),
        fetchImgDataUrl(`/api/userimage/profile/data?userId=${encodeURIComponent(partyId)}`),
        fetch(`/api/user-preferences/pdf_tagline?userId=${encodeURIComponent(partyId)}`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
        fetch(`/api/user-preferences/profile_pic_offset_y?userId=${encodeURIComponent(partyId)}`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
        fetch(`api/profile/profiledata?query=${partyId}`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      ])
      brandDataUrl = brand
      const offsetY = offsetRes?.value ? parseFloat(offsetRes.value) : 50
      headshotDataUrl = headshot ? await makeCircularDataUrl(headshot, 120, offsetY) : null
      pdfTagline = taglineRes?.value ?? ''
      const pd = profileRes?.data
      if (pd) {
        agentName = pd.fullName ?? ''
        agentOrg = pd.organization ?? ''
        const phone = pd.mobilePhone || pd.workPhone || ''
        agentContact = [pd.primaryEmail, phone].filter(Boolean).join('  |  ')
      }
    }

    const headerH = 52 // taller to fit name + contact + tagline
    const contentTop = headerH + 4

    const drawHeader = () => {
      pdf.setFillColor(76, 44, 143)
      pdf.rect(0, 0, pageWidth, headerH, 'F')
      pdf.setTextColor(255, 255, 255)

      // Headshot (circular) on the left
      const hsSize = 26
      const hsX = 6
      const hsY = (headerH - hsSize) / 2
      const textX = headshotDataUrl ? hsX + hsSize + 4 : margin
      if (headshotDataUrl) {
        pdf.addImage(headshotDataUrl, 'PNG', hsX, hsY, hsSize, hsSize)
      }

      // Brand image right-aligned
      const brandW = 38
      const brandH = 19
      if (brandDataUrl) {
        const fmt = brandDataUrl.startsWith('data:image/png')
          ? 'PNG'
          : brandDataUrl.startsWith('data:image/webp')
            ? 'WEBP'
            : 'JPEG'
        pdf.addImage(brandDataUrl, fmt, pageWidth - margin - brandW, 6, brandW, brandH)
      }
      const textMaxW = brandDataUrl
        ? pageWidth - margin - brandW - textX - 4
        : pageWidth - textX - margin

      // Organisation (replaces title in header)
      pdf.setFontSize(11)
      pdf.setFont('helvetica', 'bold')
      pdf.text(agentOrg || 'REINZ', textX, 14, { maxWidth: textMaxW })

      // Agent name
      if (agentName) {
        pdf.setFontSize(9)
        pdf.setFont('helvetica', 'bold')
        pdf.text(agentName, textX, 25, { maxWidth: textMaxW })
      }

      // Contact
      if (agentContact) {
        pdf.setFontSize(7)
        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(220, 210, 255)
        pdf.text(agentContact, textX, 34, { maxWidth: textMaxW })
      }

      // Tagline
      if (pdfTagline) {
        pdf.setFontSize(7)
        pdf.setFont('helvetica', 'bolditalic')
        pdf.setTextColor(255, 255, 255)
        pdf.text(pdfTagline, textX, 44, { maxWidth: textMaxW })
      }

      if (!brandDataUrl) {
        pdf.setFontSize(7)
        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(200, 200, 200)
        pdf.text(
          new Date().toLocaleDateString('en-NZ', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }),
          pageWidth - margin,
          14,
          { align: 'right' },
        )
      }

      pdf.setTextColor(0, 0, 0)
    }
    const maxContentH = pageHeight - contentTop - 10
    // Build cut points using pre-capture measurements scaled to canvas pixels
    const domScale = canvas.width / contentEl.offsetWidth
    const cutPointsCanvas = preCutPoints.map((domY) => domY * domScale)
    const forceBreakRows = preForceBreakTops
      .map((domY) => Math.round(domY * domScale))
      .sort((a, b) => a - b)
    const slices: { start: number; end: number }[] = []
    let startRow = 0
    while (startRow < canvas.height) {
      const maxSliceH = (maxContentH * canvas.width) / imgWidth
      // Check for a forced break that falls within this potential slice
      const nextForced = forceBreakRows.find((r) => r > startRow)
      const hardEnd = Math.min(startRow + maxSliceH, canvas.height)
      if (nextForced !== undefined && nextForced <= hardEnd) {
        if (nextForced > startRow) slices.push({ start: startRow, end: nextForced })
        startRow = nextForced
        forceBreakRows.splice(forceBreakRows.indexOf(nextForced), 1)
        continue
      }
      if (hardEnd >= canvas.height) {
        slices.push({ start: startRow, end: canvas.height })
        break
      }
      const validCuts = cutPointsCanvas.filter((p) => p > startRow && p <= hardEnd)
      const cutRow = validCuts.length > 0 ? validCuts[validCuts.length - 1] : hardEnd
      slices.push({ start: startRow, end: cutRow })
      startRow = cutRow
    }
    let page = 0
    for (const slice of slices) {
      const sliceH = slice.end - slice.start
      if (sliceH <= 1) continue
      if (page > 0) pdf.addPage()
      drawHeader()
      const sliceCanvas = document.createElement('canvas')
      sliceCanvas.width = canvas.width
      sliceCanvas.height = sliceH
      sliceCanvas
        .getContext('2d')!
        .drawImage(canvas, 0, slice.start, canvas.width, sliceH, 0, 0, canvas.width, sliceH)
      const renderedH = (sliceH * imgWidth) / canvas.width
      pdf.addImage(
        sliceCanvas.toDataURL('image/png'),
        'PNG',
        margin,
        contentTop,
        imgWidth,
        renderedH,
      )
      // Draw range buttons manually — avoids html2canvas clipping on rounded elements
      const pdfScale = imgWidth / contentEl.offsetWidth
      rangeBtns.forEach((btn) => {
        const bx = margin + btn.left * pdfScale
        const by = contentTop + (btn.top - slice.start / domScale) * pdfScale
        const bw = btn.width * pdfScale
        const bh = btn.height * pdfScale
        if (by < contentTop || by > contentTop + renderedH) return // skip if outside rendered content area
        if (btn.active) {
          pdf.setFillColor(76, 44, 143)
          pdf.roundedRect(bx, by, bw, bh, 1, 1, 'F')
          pdf.setTextColor(255, 255, 255)
        } else {
          pdf.setTextColor(100, 100, 100)
        }
        pdf.setFontSize(9)
        pdf.setFont('helvetica', 'bold')
        pdf.text(btn.label, bx + bw / 2, by + bh / 2 + 3, { align: 'center' })
        pdf.setTextColor(0, 0, 0)
      })
      // Footer — report title centred at bottom of each page
      pdf.setFontSize(8)
      pdf.setFont('helvetica', 'bold')
      pdf.setTextColor(120, 100, 180)
      pdf.text(title, pageWidth / 2, pageHeight - 6, { align: 'center' })
      pdf.setTextColor(0, 0, 0)

      page++
    }
    const pdfBlob = pdf.output('blob')
    const file = new File([pdfBlob], `${fileName}.pdf`, { type: 'application/pdf' })
    const isMobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent)
    if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title })
    } else {
      const url = URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileName}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
    return null
  } catch (err: any) {
    allEls.forEach((el, i) => {
      el.style.cssText = savedCssTexts[i]
    })
    contentEl.querySelectorAll<HTMLElement>('[data-range-btn]').forEach((el) => {
      el.style.visibility = ''
    })
    contentEl.querySelectorAll<HTMLElement>('[data-share-btn]').forEach((el) => {
      el.style.visibility = ''
    })
    if (err?.name === 'AbortError') return null
    console.error('[shareInsightsPdf]', err)
    return `Could not share: ${err?.message ?? err}`
  }
}

function taNameToRegionName(taName: string): string {
  const ta = taName.toUpperCase()
  if (
    ta.includes('AUCKLAND') ||
    ta.includes('MANUKAU') ||
    ta.includes('NORTH SHORE') ||
    ta.includes('WAITAKERE') ||
    ta.includes('PAPAKURA') ||
    ta.includes('RODNEY') ||
    ta.includes('FRANKLIN')
  )
    return 'Auckland'
  if (
    ta.includes('WELLINGTON') ||
    ta.includes('LOWER HUTT') ||
    ta.includes('HUTT') ||
    ta.includes('UPPER HUTT') ||
    ta.includes('PORIRUA') ||
    ta.includes('KAPITI') ||
    ta.includes('MASTERTON') ||
    ta.includes('CARTERTON') ||
    ta.includes('SOUTH WAIRARAPA')
  )
    return 'Wellington'
  if (
    ta.includes('CHRISTCHURCH') ||
    ta.includes('SELWYN') ||
    ta.includes('WAIMAKARIRI') ||
    ta.includes('HURUNUI') ||
    ta.includes('KAIKOURA') ||
    ta.includes('TIMARU') ||
    ta.includes('WAIMATE') ||
    ta.includes('MACKENZIE')
  )
    return 'Canterbury'
  if (
    ta.includes('DUNEDIN') ||
    ta.includes('QUEENSTOWN') ||
    ta.includes('CENTRAL OTAGO') ||
    ta.includes('CLUTHA') ||
    ta.includes('WAITAKI')
  )
    return 'Otago'
  if (ta.includes('INVERCARGILL') || ta.includes('SOUTHLAND') || ta.includes('GORE'))
    return 'Southland'
  if (
    ta.includes('HAMILTON') ||
    ta.includes('WAIKATO') ||
    ta.includes('WAIPA') ||
    ta.includes('SOUTH WAIKATO') ||
    ta.includes('MATAMATA') ||
    ta.includes('THAMES') ||
    ta.includes('HAURAKI') ||
    ta.includes('OTOROHANGA') ||
    ta.includes('WAITOMO')
  )
    return 'Waikato'
  if (
    ta.includes('TAURANGA') ||
    ta.includes('WESTERN BAY') ||
    ta.includes('ROTORUA') ||
    ta.includes('KAWERAU') ||
    ta.includes('WHAKATANE') ||
    ta.includes('OPOTIKI')
  )
    return 'Bay of Plenty'
  if (
    ta.includes('HASTINGS') ||
    ta.includes('NAPIER') ||
    ta.includes('CENTRAL HAWKE') ||
    ta.includes('WAIROA')
  )
    return "Hawke's Bay"
  if (
    ta.includes('PALMERSTON NORTH') ||
    ta.includes('MANAWATU') ||
    ta.includes('RANGITIKEI') ||
    ta.includes('WHANGANUI') ||
    ta.includes('RUAPEHU') ||
    ta.includes('TARARUA') ||
    ta.includes('HOROWHENUA')
  )
    return 'Manawatu-Whanganui'
  if (
    ta.includes('NEW PLYMOUTH') ||
    ta.includes('STRATFORD') ||
    ta.includes('SOUTH TARANAKI') ||
    ta.includes('TARANAKI')
  )
    return 'Taranaki'
  if (ta.includes('MARLBOROUGH')) return 'Marlborough'
  if (ta.includes('NELSON')) return 'Nelson'
  if (ta.includes('TASMAN')) return 'Tasman'
  if (
    ta.includes('BULLER') ||
    ta.includes('GREY') ||
    ta.includes('WESTLAND') ||
    ta.includes('WEST COAST')
  )
    return 'West Coast'
  if (
    ta.includes('WHANGAREI') ||
    ta.includes('KAIPARA') ||
    ta.includes('FAR NORTH') ||
    ta.includes('NORTHLAND')
  )
    return 'Northland'
  if (ta.includes('GISBORNE')) return 'Gisborne'
  return ''
}

function saveRegionPreference(userId: string, region: string): void {
  if (!userId) return
  fetch('api/user-preferences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, preferenceKey: 'reinz_pinned_region', preferenceValue: region }),
  }).catch(() => {})
}

function formatChartDate(dateStr: string): string {
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return `${MONTH_NAMES[d.getMonth()].slice(0, 3)} '${String(d.getFullYear()).slice(2)}`
}

// ─────────────────────────────────────────────────────────────
// STEP 3: API CALL
// Fetches market data and returns only the latest record
// ─────────────────────────────────────────────────────────────

type ChartRange = '3M' | '1Y' | '5Y' | '7Y'
type CustomRange = '3M' | '6M' | '1Y'

const CUSTOM_RANGES: CustomRange[] = ['3M', '6M', '1Y']

function getCustomDateFrom(range: CustomRange, to: Date): Date {
  switch (range) {
    case '3M':
      return new Date(to.getFullYear(), to.getMonth() - 2, 1)
    case '6M':
      return new Date(to.getFullYear(), to.getMonth() - 5, 1)
    case '1Y':
      return new Date(to.getFullYear() - 1, to.getMonth() + 1, 1)
  }
}

function getDateFromForRange(range: ChartRange, baseDateTo: Date): Date {
  switch (range) {
    case '3M':
      return new Date(baseDateTo.getFullYear(), baseDateTo.getMonth() - 2, 1)
    case '1Y':
      return new Date(baseDateTo.getFullYear() - 1, baseDateTo.getMonth() + 1, 1)
    case '5Y':
      return new Date(baseDateTo.getFullYear() - 5, baseDateTo.getMonth() + 1, 1)
    case '7Y':
      return new Date(baseDateTo.getFullYear() - 7, baseDateTo.getMonth() + 1, 1)
  }
}

// Clamp dateFrom to the backend tier 3 limit (10 years + 1 month buffer from today)
function clampDateFrom(dateFrom: Date): Date {
  const limit = new Date()
  limit.setFullYear(limit.getFullYear() - 10)
  limit.setMonth(limit.getMonth() - 1)
  return dateFrom < limit ? limit : dateFrom
}

function getFrequencyForRange(range: ChartRange): string {
  switch (range) {
    case '3M':
      return '3'
    case '1Y':
      return '12'
    case '5Y':
      return '3'
    case '7Y':
      return '12'
  }
}

async function fetchNationalData(
  dateTo: Date,
  dateFrom: Date,
  frequency: string,
): Promise<MarketInsightRecord[]> {
  const params = new URLSearchParams({
    GroupingType: '1',
    Grouping1: '0',
    Grouping2: 'all',
    Frequency: frequency,
    DateFrom: toApiDateString(dateFrom),
    DateTo: toApiDateString(new Date(dateTo.getFullYear(), dateTo.getMonth() + 1, 1)),
  })

  const res = await fetch(`api/report/market-insights?${params}`)

  if (!res.ok) throw new Error(`API error: ${res.status}`)

  const records: MarketInsightRecord[] = await res.json()
  if (!records || records.length === 0) throw new Error('No data available')

  return records.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
}
// ─────────────────────────────────────────────────────────────
// STEP 4: METRIC CARD COMPONENT
// Reusable card that displays a single metric
// ─────────────────────────────────��───────────────────────────

function MetricCard({
  label,
  value,
  // change,
  // suffix = ""
}: {
  label: string
  value: string | number
  // change?: number;
  // suffix?: string;
}) {
  // const TrendIcon = change === undefined ? null : change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
  // const trendColor = change === undefined ? "" : change > 0 ? "text-emerald-600" : change < 0 ? "text-red-500" : "text-muted-foreground";

  return (
    <Card className="p-4">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <div className="flex items-end justify-between">
        <p className="text-xl font-bold text-foreground">
          {value}
          {/* {value}{suffix} */}
        </p>
        {/* {change !== undefined && TrendIcon && (
          <div className={`flex items-center gap-1 text-xs ${trendColor}`}>
            <TrendIcon className="w-3 h-3" />
            <span>{Math.abs(change)}%</span>
          </div>
        )} */}
      </div>
    </Card>
  )
}
const RANGE_LABELS: ChartRange[] = ['3M', '1Y', '5Y', '7Y']
const SUBURB_COLORS = [
  '#2563eb',
  '#dc2626',
  '#16a34a',
  '#d97706',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
]
const RANGE_MONTHS: Record<ChartRange, number> = { '3M': 3, '1Y': 12, '5Y': 60, '7Y': 84 }

// Months of data available between the tier limit and the selected dateTo
function getAvailableMonths(dateTo: Date): number {
  const limit = new Date()
  limit.setFullYear(limit.getFullYear() - 10)
  limit.setMonth(limit.getMonth() - 1)
  return (dateTo.getFullYear() - limit.getFullYear()) * 12 + (dateTo.getMonth() - limit.getMonth())
}

// Largest range that fits within the available months
function largestValidRange(dateTo: Date): ChartRange {
  const available = getAvailableMonths(dateTo)
  if (available >= 84) return '7Y'
  if (available >= 60) return '5Y'
  if (available >= 12) return '1Y'
  return '3M'
}

function PriceTrendChart({
  records,
  chartRange,
  onRangeChange,
  dateTo,
}: {
  records: MarketInsightRecord[]
  chartRange: ChartRange
  onRangeChange: (r: ChartRange) => void
  dateTo: Date
}) {
  if (records.length < 2) return null
  const availableMonths = getAvailableMonths(dateTo)
  const data = records.map((r) => ({
    date: formatChartDate(r.Date),
    price: r.MedianSalePrice,
    sales: r.NoOfSales,
  }))
  const prices = data.map((d) => d.price).filter((p) => p > 0)
  const minPrice = Math.min(...prices)
  const maxPrice = Math.max(...prices)
  const pad = (maxPrice - minPrice) * 0.15 || maxPrice * 0.1
  const yMin = Math.max(0, Math.floor((minPrice - pad) / 50_000) * 50_000)
  const yMax = Math.ceil((maxPrice + pad) / 50_000) * 50_000
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-muted-foreground">Median Sale Price Trend</p>
        <div className="flex gap-1">
          {RANGE_LABELS.map((r) => {
            const disabled = RANGE_MONTHS[r] > availableMonths
            return (
              <button
                key={r}
                onClick={() => {
                  if (!disabled) {
                    onRangeChange(r)
                    trackEvent('market_chart_range_changed', { range: r })
                  }
                }}
                disabled={disabled}
                data-range-btn={r}
                data-range-active={chartRange === r ? 'true' : 'false'}
                className={`w-8 py-1 text-xs rounded font-medium text-center transition-colors ${
                  disabled
                    ? 'text-muted-foreground/30 cursor-not-allowed'
                    : chartRange === r
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {r}
              </button>
            )
          })}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.18} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: '#94a3b8' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[yMin, yMax]}
            tickFormatter={(v) =>
              v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${(v / 1_000).toFixed(0)}K`
            }
            tick={{ fontSize: 10, fill: '#94a3b8' }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            formatter={(value: number) =>
              value >= 1_000_000
                ? [`$${(value / 1_000_000).toFixed(2)}M`, 'Median Price']
                : [`$${(value / 1_000).toFixed(0)}K`, 'Median Price']
            }
            labelStyle={{ fontSize: 11, fontWeight: 600 }}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
          />
          <Area
            type="monotone"
            dataKey="price"
            stroke="#2563eb"
            strokeWidth={2}
            fill="url(#priceGrad)"
            dot={false}
            activeDot={{ r: 4, fill: '#2563eb' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  )
}
// ─────────────────────────────────────────────────────────────
// STEP 5: NATIONAL VIEW
// Manages state and renders the date picker + metric cards
// ─────────────────────────────────────────────────────────────

function NationalView({ partyId, latestMonth }: { partyId: string; latestMonth: Date }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isSharing, setIsSharing] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)

  const handleShare = async () => {
    if (!contentRef.current) return
    setIsSharing(true)
    setShareError(null)
    trackEvent('share_pdf', { view: 'national' })
    const err = await shareInsightsPdf(
      contentRef.current,
      'REINZ Market Insights — National',
      'national_insights',
      partyId,
    )
    setShareError(err)
    setIsSharing(false)
  }

  const [dateTo, setDateTo] = useState<Date>(latestMonth)

  // Keep dateTo in sync if latestMonth resolves after mount
  useEffect(() => {
    setDateTo((prev) => (prev > latestMonth ? latestMonth : prev))
  }, [latestMonth.getTime()]) // eslint-disable-line react-hooks/exhaustive-deps

  const [records, setRecords] = useState<MarketInsightRecord[]>([])
  const [metricRecord, setMetricRecord] = useState<MarketInsightRecord | null>(null)
  const [chartRange, setChartRange] = useState<ChartRange>('1Y')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const currentMonthLabel = `${MONTH_NAMES[dateTo.getMonth()]} ${dateTo.getFullYear()}`

  // Chart data — frequency varies by range
  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const dateFrom = clampDateFrom(getDateFromForRange(chartRange, dateTo))
        const data = await fetchNationalData(dateTo, dateFrom, getFrequencyForRange(chartRange))
        setRecords(data.filter((r) => new Date(r.Date) >= dateFrom))
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [dateTo.getTime(), chartRange])

  // Metric cards — always fetch with frequency=1 (annual)
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchNationalData(dateTo, dateTo, '1')
        const record = data.at(-1) ?? null
        const d = record ? new Date(record.Date) : null
        setMetricRecord(
          record &&
            d &&
            d.getFullYear() === dateTo.getFullYear() &&
            d.getMonth() === dateTo.getMonth() &&
            (record.MedianSalePrice ?? 0) > 0
            ? record
            : null,
        )
      } catch {
        /* silent — metric cards just stay empty */
      }
    }
    load()
  }, [dateTo.getTime()])

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">National Snapshot</h2>
        <MonthPicker
          value={dateTo}
          latestMonth={latestMonth}
          onChange={(newDate) => {
            setDateTo(newDate)
            if (RANGE_MONTHS[chartRange] > getAvailableMonths(newDate))
              setChartRange(largestValidRange(newDate))
          }}
        />
      </div>
      {shareError && <p className="text-xs text-destructive">{shareError}</p>}

      <div ref={contentRef}>
        {/* Loading state */}
        {loading && (
          <div className="grid grid-cols-2 gap-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-4 h-20">
                <div className="h-3 bg-muted rounded w-1/2 mb-3" />
                <div className="h-6 bg-muted rounded w-2/3" />
              </Card>
            ))}
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <Card className="p-4 border-red-200 bg-red-50">
            <p className="text-sm text-red-600 font-medium">Failed to load data</p>
            <p className="text-xs text-red-500 mt-1">{error}</p>
          </Card>
        )}

        {/* No data state */}
        {!loading && !error && !metricRecord && (
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">
              No data available for {currentMonthLabel}
            </p>
          </Card>
        )}

        {/* 4 Metric Cards — only shown when data is ready */}
        {!loading && !error && metricRecord && (
          <>
            <Card className="p-4 relative">
              <button
                onClick={handleShare}
                disabled={isSharing}
                data-share-btn
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50"
                title="Share"
              >
                {isSharing ? (
                  <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4 text-primary" />
                )}
              </button>
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">New Zealand</h3>
              </div>
              <p className="text-2xl font-bold">{formatPrice(metricRecord.MedianSalePrice)}</p>
            </Card>
            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                label="Median Sale Price"
                value={formatPrice(metricRecord.MedianSalePrice)}
              />
              <MetricCard label="Monthly Sales" value={metricRecord.NoOfSales.toLocaleString()} />
              <MetricCard label="Days to Sell" value={`${metricRecord.DaysToSell} days`} />
              <MetricCard
                label="Sale vs Valuation"
                value={`${(metricRecord.PercSaleToValDiff * 100).toFixed(0)}%`}
              />
              <MetricCard
                label="Median Valuation"
                value={formatPrice(metricRecord.MedianValuation)}
              />
              <MetricCard
                label="Median List Price"
                value={formatPrice(metricRecord.MedianListPrice)}
              />
              <MetricCard
                label="List Price vs Valuation"
                value={`${(metricRecord.PercListPriceToValDiff * 100).toFixed(0)}%`}
              />
              <MetricCard
                label="House Price Index"
                value={metricRecord.HousePriceIndex.toString()}
              />
            </div>
            <PriceTrendChart
              records={records}
              chartRange={chartRange}
              onRangeChange={setChartRange}
              dateTo={dateTo}
            />
            {/* Summary card */}
            <Card className="p-4 bg-blue-50 border-l-4 border-l-blue-500">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                Market Summary · {currentMonthLabel}
              </p>
              <p className="text-sm text-blue-900 leading-relaxed">
                In <strong>{currentMonthLabel}</strong>, the national median sale price was{' '}
                <strong>{formatPrice(metricRecord.MedianSalePrice)}</strong>, with homes selling in{' '}
                <strong>{metricRecord.DaysToSell} days</strong>.
                {metricRecord.PercSalePriceDiffLastPeriod != null && (
                  <>
                    {' '}
                    Sales prices are{' '}
                    <strong
                      className={
                        metricRecord.PercSalePriceDiffLastPeriod >= 0
                          ? 'text-emerald-600'
                          : 'text-red-500'
                      }
                    >
                      {metricRecord.PercSalePriceDiffLastPeriod === 0
                        ? 'unchanged'
                        : metricRecord.PercSalePriceDiffLastPeriod > 0
                          ? 'up'
                          : 'down'}{' '}
                      {metricRecord.PercSalePriceDiffLastPeriod !== 0
                        ? `${Math.abs(metricRecord.PercSalePriceDiffLastPeriod)}%`
                        : ''}
                    </strong>{' '}
                    compared to last period.
                  </>
                )}
              </p>
            </Card>
          </>
        )}
      </div>
      {/* end contentRef */}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// STEP 7: REGIONAL VIEW (unchanged from original)
// ─────────────────────────────────────────────────────────────
// interface RegionData {
//   name: string;
//   medianPrice: number;
//   yoyChange: number;
//   salesVolume: number;
//   daysToSell: number;
//   clearanceRate: number;
//   insight: string;
// }

// const regionData: Record<string, RegionData> = {
//   auckland: {
//     name: "Auckland",
//     medianPrice: 1020000,
//     yoyChange: 2.1,
//     salesVolume: 2847,
//     daysToSell: 34,
//     clearanceRate: 68,
//     insight: "Auckland median prices stabilised this quarter, with first-home buyer activity increasing in South Auckland. North Shore continues to show resilience with strong family home demand.",
//   },
//   wellington: {
//     name: "Wellington",
//     medianPrice: 785000,
//     yoyChange: -1.2,
//     salesVolume: 1243,
//     daysToSell: 38,
//     clearanceRate: 62,
//     insight: "Wellington market showing signs of stabilisation after previous quarter softness. Central suburbs remain competitive, with increased investor interest in Lower Hutt.",
//   },
//   christchurch: {
//     name: "Christchurch",
//     medianPrice: 695000,
//     yoyChange: 4.5,
//     salesVolume: 1586,
//     daysToSell: 28,
//     clearanceRate: 72,
//     insight: "Christchurch leads regional growth with strong infrastructure investment driving demand. New subdivisions in Rolleston continue to attract young families.",
//   },
//   tauranga: {
//     name: "Tauranga",
//     medianPrice: 920000,
//     yoyChange: 1.8,
//     salesVolume: 678,
//     daysToSell: 31,
//     clearanceRate: 65,
//     insight: "Lifestyle migration continues to support Tauranga prices. Mount Maunganui remains the premium location with limited stock driving competition.",
//   },
//   hamilton: {
//     name: "Hamilton",
//     medianPrice: 725000,
//     yoyChange: 3.2,
//     salesVolume: 892,
//     daysToSell: 29,
//     clearanceRate: 70,
//     insight: "Hamilton benefits from Auckland spillover demand. Strong rental yields attracting investors while first-home buyers focus on outer suburbs.",
//   },
// };
// ─── Types ───────────────────────────────────────────────────
interface RegionLookup {
  regionId: number
  region: string
}

// ─── API Functions ────────────────────────────────────────────

// Step 1: Fetch all regions from your lookup API
async function fetchAllRegions(): Promise<RegionLookup[]> {
  const res = await fetch(`api/report/region?region=all`)
  if (!res.ok) throw new Error(`Failed to fetch regions: ${res.status}`)
  return res.json()
}

// Step 2: Fetch market insights using regionId
async function fetchRegionalData(
  regionId: number,
  dateTo: Date,
  dateFrom: Date,
  frequency: string,
): Promise<MarketInsightRecord[]> {
  const params = new URLSearchParams({
    GroupingType: '2',
    Grouping1: String(regionId),
    Grouping2: 'all',
    Frequency: frequency,
    DateFrom: toApiDateString(dateFrom),
    DateTo: toApiDateString(new Date(dateTo.getFullYear(), dateTo.getMonth() + 1, 1)),
  })

  const res = await fetch(`api/report/market-insights?${params}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)

  const records: MarketInsightRecord[] = await res.json()
  if (!records || records.length === 0) throw new Error('No data available for this region')
  return records.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
}
function RegionalView({ partyId, latestMonth }: { partyId: string; latestMonth: Date }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isSharing, setIsSharing] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)
  const handleShare = async () => {
    if (!contentRef.current) return
    setIsSharing(true)
    setShareError(null)
    trackEvent('share_pdf', { view: 'regional' })
    const err = await shareInsightsPdf(
      contentRef.current,
      'REINZ Market Insights — Regional',
      'regional_insights',
      partyId,
    )
    setShareError(err)
    setIsSharing(false)
  }
  //const [selectedRegion, setSelectedRegion] = useState("auckland");
  //const region = regionData[selectedRegion];

  // return (
  //   <div className="space-y-4">
  //     <div className="flex items-center justify-between gap-3">
  //       <h2 className="text-lg font-semibold">Regional Details</h2>
  //       <Select value={selectedRegion} onValueChange={setSelectedRegion}>
  //         <SelectTrigger className="w-40">
  //           <SelectValue />
  //         </SelectTrigger>
  //         <SelectContent>
  //           <SelectItem value="auckland">Auckland</SelectItem>
  //           <SelectItem value="wellington">Wellington</SelectItem>
  //           <SelectItem value="christchurch">Christchurch</SelectItem>
  //           <SelectItem value="tauranga">Tauranga</SelectItem>
  //           <SelectItem value="hamilton">Hamilton</SelectItem>
  //         </SelectContent>
  //       </Select>
  //     </div>

  //     <Card className="p-4">
  //       <div className="flex items-center gap-2 mb-2">
  //         <MapPin className="w-5 h-5 text-primary" />
  //         <h3 className="text-lg font-semibold">{region.name}</h3>
  //       </div>
  //       <p className="text-2xl font-bold mb-1">
  //         {formatPrice(region.medianPrice)}
  //       </p>
  //     </Card>

  //     <div className="grid grid-cols-3 gap-3">
  //       <Card className="p-3 text-center">
  //         <p className="text-xs text-muted-foreground">Sales Volume</p>
  //         <p className="text-lg font-bold">
  //           {region.salesVolume.toLocaleString()}
  //         </p>
  //       </Card>
  //       <Card className="p-3 text-center">
  //         <p className="text-xs text-muted-foreground">Days to Sell</p>
  //         <p className="text-lg font-bold">{region.daysToSell}</p>
  //       </Card>
  //       <Card className="p-3 text-center">
  //         <p className="text-xs text-muted-foreground">Clearance</p>
  //         <p className="text-lg font-bold">{region.clearanceRate}%</p>
  //       </Card>
  //     </div>

  //     <Card className="p-4 border-l-4 border-l-accent">
  //       <h3 className="font-semibold mb-2">Regional Insight</h3>
  //       <p className="text-sm text-muted-foreground leading-relaxed">
  //         {region.insight}
  //       </p>
  //     </Card>
  //   </div>
  // );
  const [regions, setRegions] = useState<RegionLookup[]>([])
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null)
  const [dateTo, setDateTo] = useState<Date>(latestMonth)
  const userSelectedDate = useRef(false)
  const [records, setRecords] = useState<MarketInsightRecord[]>([])

  useEffect(() => {
    setDateTo((prev) => {
      if (prev > latestMonth) return latestMonth
      if (!userSelectedDate.current) return latestMonth
      return prev
    })
  }, [latestMonth.getTime()]) // eslint-disable-line react-hooks/exhaustive-deps
  const [metricRecord, setMetricRecord] = useState<MarketInsightRecord | null>(null)
  const [chartRange, setChartRange] = useState<ChartRange>('1Y')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load region list + derive default region from pinned suburb on mount
  useEffect(() => {
    Promise.all([
      fetchAllRegions(),
      partyId
        ? fetch(`api/user-preferences?userId=${encodeURIComponent(partyId)}`)
            .then((r) => (r.ok ? r.json() : []))
            .catch(() => [])
        : Promise.resolve([]),
    ])
      .then(([data, prefs]) => {
        const filtered = data.filter((r) => r.regionId < 50)
        setRegions(filtered)

        const typedPrefs = prefs as { preferenceKey: string; preferenceValue: string }[]

        // Derive region from pinned suburb's taName (primary source)
        const suburbRaw =
          typedPrefs.find((p) => p.preferenceKey === 'reinz_pinned_suburb')?.preferenceValue ?? null
        let derivedRegion: string | null = null
        if (suburbRaw) {
          try {
            const suburb = JSON.parse(suburbRaw) as { taName: string | null }
            if (suburb.taName) derivedRegion = taNameToRegionName(suburb.taName) || null
          } catch {
            /* ignore */
          }
        }

        // Fallback to existing saved region if suburb derivation fails
        const fallbackRegion = !derivedRegion
          ? (typedPrefs.find((p) => p.preferenceKey === 'reinz_pinned_region')?.preferenceValue ??
            null)
          : null

        const regionToUse = derivedRegion ?? fallbackRegion
        const matched = regionToUse
          ? filtered.find((r) => r.region.toLowerCase().includes(regionToUse.toLowerCase()))
          : null
        setSelectedRegionId(matched?.regionId ?? filtered[0]?.regionId ?? null)

        // Save derived region to DB so HomeScreen "Home" toggle can use it
        if (derivedRegion && partyId) {
          saveRegionPreference(partyId, derivedRegion)
        }
      })
      .catch(() => setError('Failed to load regions'))
  }, [partyId])

  // Chart data — frequency varies by range
  useEffect(() => {
    if (!selectedRegionId) return
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const dateFrom = clampDateFrom(getDateFromForRange(chartRange, dateTo))
        const data = await fetchRegionalData(
          selectedRegionId,
          dateTo,
          dateFrom,
          getFrequencyForRange(chartRange),
        )
        setRecords(data.filter((r) => new Date(r.Date) >= dateFrom))
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [selectedRegionId, dateTo.getTime(), chartRange])

  // Metric cards — always fetch with frequency=1 (annual)
  useEffect(() => {
    if (!selectedRegionId) return
    const load = async () => {
      try {
        const data = await fetchRegionalData(selectedRegionId, dateTo, dateTo, '1')
        const record = data.at(-1) ?? null
        const d = record ? new Date(record.Date) : null
        setMetricRecord(
          record &&
            d &&
            d.getFullYear() === dateTo.getFullYear() &&
            d.getMonth() === dateTo.getMonth() &&
            (record.MedianSalePrice ?? 0) > 0
            ? record
            : null,
        )
      } catch {
        /* silent */
      }
    }
    load()
  }, [selectedRegionId, dateTo.getTime()])

  const currentMonthLabel = `${MONTH_NAMES[dateTo.getMonth()]} ${dateTo.getFullYear()}`
  const selectedRegionName = regions.find((r) => r.regionId === selectedRegionId)?.region ?? ''

  return (
    <div className="space-y-4">
      {shareError && <p className="text-xs text-destructive">{shareError}</p>}
      {/* Header with region selector + date picker — outside contentRef so it's not captured */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <MonthPicker
            value={dateTo}
            latestMonth={latestMonth}
            onChange={(newDate) => {
              userSelectedDate.current = true
              setDateTo(newDate)
              if (RANGE_MONTHS[chartRange] > getAvailableMonths(newDate))
                setChartRange(largestValidRange(newDate))
            }}
          />
        </div>
        <div className="flex-1">
          <Select
            value={String(selectedRegionId)}
            onValueChange={(val) => {
              setSelectedRegionId(Number(val))
              trackEvent('market_region_changed', {
                region: regions.find((r) => r.regionId === Number(val))?.region ?? val,
              })
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select region" />
            </SelectTrigger>
            <SelectContent>
              {regions.map((r) => (
                <SelectItem key={r.regionId} value={String(r.regionId)}>
                  {r.region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div ref={contentRef}>
        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-2 gap-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-4 h-20">
                <div className="h-3 bg-muted rounded w-1/2 mb-3" />
                <div className="h-6 bg-muted rounded w-2/3" />
              </Card>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <Card className="p-4 border-red-200 bg-red-50">
            <p className="text-sm text-red-600 font-medium">Failed to load data</p>
            <p className="text-xs text-red-500 mt-1">{error}</p>
          </Card>
        )}

        {/* No data */}
        {!loading && !error && !metricRecord && (
          <Card className="p-4 text-center">
            <p className="text-sm text-muted-foreground">
              No data available for {currentMonthLabel}
            </p>
          </Card>
        )}

        {/* Data */}
        {!loading && !error && metricRecord && (
          <>
            <Card className="p-4 relative">
              <button
                onClick={handleShare}
                disabled={isSharing}
                data-share-btn
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50"
                title="Share"
              >
                {isSharing ? (
                  <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4 text-primary" />
                )}
              </button>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">{selectedRegionName}</h3>
              </div>
              <p className="text-2xl font-bold">{formatPrice(metricRecord.MedianSalePrice)}</p>
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <MetricCard label="Monthly Sales" value={metricRecord.NoOfSales.toLocaleString()} />
              <MetricCard label="Days to Sell" value={`${metricRecord.DaysToSell} days`} />
              <MetricCard
                label="Median Valuation"
                value={formatPrice(metricRecord.MedianValuation)}
              />
              <MetricCard
                label="Sale vs Valuation"
                value={`${(metricRecord.PercSaleToValDiff * 100).toFixed(0)}%`}
              />
            </div>

            <PriceTrendChart
              records={records}
              chartRange={chartRange}
              onRangeChange={setChartRange}
              dateTo={dateTo}
            />

            {/* Summary card */}
            <Card className="p-4 bg-blue-50 border-l-4 border-l-blue-500">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                Market Summary · {currentMonthLabel}
              </p>
              <p className="text-sm text-blue-900 leading-relaxed">
                In <strong>{currentMonthLabel}</strong>, {selectedRegionName}'s median sale price
                was <strong>{formatPrice(metricRecord.MedianSalePrice)}</strong>, with homes selling
                in <strong>{metricRecord.DaysToSell} days</strong>.
                {metricRecord.PercSalePriceDiffLastPeriod != null && (
                  <>
                    {' '}
                    Sales prices are{' '}
                    <strong
                      className={
                        metricRecord.PercSalePriceDiffLastPeriod >= 0
                          ? 'text-emerald-600'
                          : 'text-red-500'
                      }
                    >
                      {metricRecord.PercSalePriceDiffLastPeriod === 0
                        ? 'unchanged'
                        : metricRecord.PercSalePriceDiffLastPeriod > 0
                          ? 'up'
                          : 'down'}{' '}
                      {metricRecord.PercSalePriceDiffLastPeriod !== 0
                        ? `${Math.abs(metricRecord.PercSalePriceDiffLastPeriod)}%`
                        : ''}
                    </strong>{' '}
                    compared to last period.
                  </>
                )}
              </p>
            </Card>
          </>
        )}
      </div>
      {/* end contentRef */}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// SUBURB VIEW
// ─────────────────────────────────────────────────────────────
interface SuburbLookup {
  suburbID: number
  suburbName: string
  taName: string | null
}

async function fetchSuburbSuggestions(query: string): Promise<SuburbLookup[]> {
  const res = await fetch(`/api/report/suburbs?query=${encodeURIComponent(query)}`)
  if (!res.ok) throw new Error('Failed to fetch suburbs')
  return res.json()
}

interface SuburbSaleRecord {
  saleID: number
  suburbName: string | null
  saleDate: string
  salePrice: number | null
  salePriceRange: string | null
  saleMethod: string | null
  category: string | null
  bedrooms: number | null
  bathrooms: number | null
  floorArea: number | null
  landArea: number | null
  sellDays: number | null
  freeholdStatus: string | null
  isNewDwelling: boolean | null
  isSettled: boolean | null
}

async function fetchSuburbSales(
  suburbId: number,
  dateFrom: Date,
  dateTo: Date,
): Promise<SuburbSaleRecord[]> {
  // DateTo is exclusive (< in SQL), so pass first of next month
  const dateToExclusive = new Date(dateTo.getFullYear(), dateTo.getMonth() + 1, 1)
  const params = new URLSearchParams({
    SuburbId: String(suburbId),
    DateFrom: toApiDateString(dateFrom),
    DateTo: toApiDateString(dateToExclusive),
  })
  const res = await fetch(`/api/report/suburb-sales?${params}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json()
}

async function fetchSuburbData(
  suburbId: number,
  dateTo: Date,
  dateFrom: Date,
  frequency: string,
): Promise<MarketInsightRecord[]> {
  const params = new URLSearchParams({
    GroupingType: '5',
    Grouping1: String(suburbId),
    Grouping2: 'all',
    Frequency: frequency,
    DateFrom: toApiDateString(dateFrom),
    DateTo: toApiDateString(new Date(dateTo.getFullYear(), dateTo.getMonth() + 1, 1)),
  })
  const res = await fetch(`api/report/market-insights?${params}`)
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  const records: MarketInsightRecord[] = await res.json()
  if (!records || records.length === 0) throw new Error('No data available for this suburb')
  return records.sort((a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime())
}

function SuburbView({ partyId, latestMonth }: { partyId: string; latestMonth: Date }) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [isSharing, setIsSharing] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)
  const handleShare = async () => {
    if (!contentRef.current) return
    setIsSharing(true)
    setShareError(null)
    trackEvent('share_pdf', { view: 'suburb', suburb: selectedSuburb?.suburbName })
    const err = await shareInsightsPdf(
      contentRef.current,
      'REINZ Market Insights — Suburb',
      'suburb_insights',
      partyId,
    )
    setShareError(err)
    setIsSharing(false)
  }
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<SuburbLookup[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [selectedSuburb, setSelectedSuburb] = useState<SuburbLookup | null>(null)
  const [dateTo, setDateTo] = useState<Date>(latestMonth)
  const userSelectedDate = useRef(false)
  const [records, setRecords] = useState<MarketInsightRecord[]>([])

  useEffect(() => {
    setDateTo((prev) => {
      if (prev > latestMonth) return latestMonth
      if (!userSelectedDate.current) return latestMonth
      return prev
    })
  }, [latestMonth.getTime()]) // eslint-disable-line react-hooks/exhaustive-deps
  const [metricRecord, setMetricRecord] = useState<MarketInsightRecord | null>(null)
  const [chartRange, setChartRange] = useState<ChartRange>('1Y')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Individual sales state
  const [sales, setSales] = useState<SuburbSaleRecord[]>([])
  const [salesLoading, setSalesLoading] = useState(false)
  const PRICE_FALLBACK = 10_000_000
  const [sliderMax, setSliderMax] = useState(PRICE_FALLBACK)
  const [priceRange, setPriceRange] = useState<[number, number]>([0, PRICE_FALLBACK])
  const [filterBedrooms, setFilterBedrooms] = useState('any')
  const [filterBathrooms, setFilterBathrooms] = useState('any')
  const [filterCategory, setFilterCategory] = useState('any')
  const [filterSaleMethod, setFilterSaleMethod] = useState('any')

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Load default suburb from user preference on mount, fallback to Ponsonby
  useEffect(() => {
    if (!partyId) {
      fetchSuburbSuggestions('Ponsonby')
        .then((d) => {
          if (d.length > 0) setSelectedSuburb(d[0])
        })
        .catch(() => {})
      return
    }
    fetch(`api/user-preferences?userId=${encodeURIComponent(partyId)}`)
      .then((r) => (r.ok ? r.json() : []))
      .then(async (prefs: { preferenceKey: string; preferenceValue: string }[]) => {
        const raw =
          prefs.find((p) => p.preferenceKey === 'reinz_pinned_suburb')?.preferenceValue ?? null
        if (raw) {
          try {
            setSelectedSuburb(JSON.parse(raw) as SuburbLookup)
            return
          } catch {
            /* fall through */
          }
        }
        const fallback = await fetchSuburbSuggestions('Ponsonby')
        if (fallback.length > 0) setSelectedSuburb(fallback[0])
      })
      .catch(() => {
        fetchSuburbSuggestions('Ponsonby')
          .then((d) => {
            if (d.length > 0) setSelectedSuburb(d[0])
          })
          .catch(() => {})
      })
  }, [partyId])

  // Fetch suggestions as user types inside dropdown
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([])
      return
    }
    fetchSuburbSuggestions(query)
      .then((data) => setSuggestions(data.slice(0, 5)))
      .catch(() => setSuggestions([]))
  }, [query])

  // Chart data — frequency varies by range
  useEffect(() => {
    if (!selectedSuburb) return
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const dateFrom = clampDateFrom(getDateFromForRange(chartRange, dateTo))
        const data = await fetchSuburbData(
          selectedSuburb.suburbID,
          dateTo,
          dateFrom,
          getFrequencyForRange(chartRange),
        )
        setRecords(data.filter((r) => new Date(r.Date) >= dateFrom))
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [selectedSuburb?.suburbID, dateTo.getTime(), chartRange])

  // Metric cards — always fetch with frequency=1 (annual)
  useEffect(() => {
    if (!selectedSuburb) return
    const load = async () => {
      try {
        const data = await fetchSuburbData(selectedSuburb.suburbID, dateTo, dateTo, '1')
        const record = data.at(-1) ?? null
        const d = record ? new Date(record.Date) : null
        setMetricRecord(
          record &&
            d &&
            d.getFullYear() === dateTo.getFullYear() &&
            d.getMonth() === dateTo.getMonth() &&
            (record.MedianSalePrice ?? 0) > 0
            ? record
            : null,
        )
      } catch {
        /* silent */
      }
    }
    load()
  }, [selectedSuburb?.suburbID, dateTo.getTime()])

  // Individual sales — fetch for selected month window
  useEffect(() => {
    if (!selectedSuburb) return
    const load = async () => {
      setSalesLoading(true)
      try {
        const salesMonth = dateTo
        const data = await fetchSuburbSales(selectedSuburb.suburbID, salesMonth, salesMonth)
        setSales(data)
        const prices = data.map((s) => s.salePrice ?? 0).filter((p) => p > 0)
        const computedMax =
          prices.length > 0 ? Math.ceil(Math.max(...prices) / 500_000) * 500_000 : PRICE_FALLBACK
        setSliderMax(computedMax)
        setPriceRange([0, computedMax])
        setFilterBedrooms('any')
        setFilterBathrooms('any')
        setFilterCategory('any')
        setFilterSaleMethod('any')
      } catch {
        setSales([])
      } finally {
        setSalesLoading(false)
      }
    }
    load()
  }, [selectedSuburb?.suburbID, dateTo.getTime()])

  const currentMonthLabel = `${MONTH_NAMES[dateTo.getMonth()]} ${dateTo.getFullYear()}`

  const salesMonthLabel = `${MONTH_NAMES[dateTo.getMonth()]} ${dateTo.getFullYear()}`

  // Derive distinct values for dropdowns from loaded sales
  const categories = Array.from(
    new Set(sales.map((s) => s.category).filter(Boolean)),
  ).sort() as string[]
  const saleMethods = Array.from(
    new Set(sales.map((s) => s.saleMethod).filter(Boolean)),
  ).sort() as string[]

  // Apply filters client-side
  const filteredSales = sales.filter((s) => {
    if (
      s.salePrice != null &&
      s.salePrice > 0 &&
      (priceRange[0] > 0 || priceRange[1] < sliderMax)
    ) {
      if (s.salePrice < priceRange[0] || s.salePrice > priceRange[1] + 49999) return false
    }
    if (filterBedrooms !== 'any') {
      const beds = s.bedrooms ?? 0
      if (filterBedrooms.endsWith('+')) {
        if (beds < Number(filterBedrooms.replace('+', ''))) return false
      } else if (beds !== Number(filterBedrooms)) return false
    }
    if (filterBathrooms !== 'any') {
      const baths = s.bathrooms ?? 0
      if (filterBathrooms === '3+') {
        if (baths < 3) return false
      } else if (baths !== Number(filterBathrooms)) return false
    }
    if (filterCategory !== 'any' && s.category !== filterCategory) return false
    if (filterSaleMethod !== 'any' && s.saleMethod !== filterSaleMethod) return false
    return true
  })

  return (
    <div className="space-y-4">
      {shareError && <p className="text-xs text-destructive">{shareError}</p>}
      {/* Header — outside contentRef so it's not captured in the PDF */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <MonthPicker
            value={dateTo}
            latestMonth={latestMonth}
            onChange={(newDate) => {
              userSelectedDate.current = true
              setDateTo(newDate)
              if (RANGE_MONTHS[chartRange] > getAvailableMonths(newDate))
                setChartRange(largestValidRange(newDate))
            }}
          />
        </div>

        {/* Custom searchable suburb picker */}
        <div className="relative flex-1" ref={dropdownRef}>
          <button
            onClick={() => {
              setIsOpen((o) => !o)
              setQuery('')
              setSuggestions([])
            }}
            className="w-full flex items-center justify-between border rounded px-3 py-2 text-sm bg-background text-foreground hover:bg-muted transition-colors"
          >
            <span className="truncate">
              {selectedSuburb ? selectedSuburb.suburbName : 'Select suburb'}
            </span>
            <span className="ml-1 text-muted-foreground">▾</span>
          </button>

          {isOpen && (
            <div className="absolute right-0 z-50 mt-1 w-full bg-background border border-border rounded-lg shadow-lg overflow-hidden">
              {/* Search inside dropdown */}
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search..."
                    className="w-full pl-7 pr-2 py-1.5 text-sm border rounded bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Suburb list — max 5 */}
              <div>
                {suggestions.length > 0 ? (
                  suggestions.map((s) => (
                    <button
                      key={s.suburbID}
                      className={`w-full text-left px-3 py-2.5 hover:bg-muted transition-colors flex items-center gap-2 ${selectedSuburb?.suburbID === s.suburbID ? 'bg-primary/10 text-primary font-medium' : ''}`}
                      onClick={() => {
                        setSelectedSuburb(s)
                        setIsOpen(false)
                        setQuery('')
                        trackEvent('suburb_selected', { suburb: s.suburbName })
                      }}
                    >
                      {selectedSuburb?.suburbID === s.suburbID && (
                        <span className="shrink-0">✓</span>
                      )}
                      <div className="flex flex-col">
                        <span className="text-sm">{s.suburbName}</span>
                        <span className="text-xs text-muted-foreground">
                          {s.taName?.replace(' District', '').replace(' City', '') ?? ''}
                        </span>
                      </div>
                    </button>
                  ))
                ) : query.length < 2 ? (
                  <p className="px-3 py-2.5 text-sm text-muted-foreground">
                    Type to search suburbs...
                  </p>
                ) : (
                  <p className="px-3 py-2.5 text-sm text-muted-foreground">No results found</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div ref={contentRef}>
        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-2 gap-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="p-4 h-20">
                <div className="h-3 bg-muted rounded w-1/2 mb-3" />
                <div className="h-6 bg-muted rounded w-2/3" />
              </Card>
            ))}
          </div>
        )}

        {/* Error */}
        {!loading &&
          error &&
          (error === 'No data available for this suburb' ? (
            <Card className="p-4 border-amber-200 bg-amber-50">
              <p className="text-sm text-amber-700 font-medium">No aggregated insights available</p>
              <p className="text-xs text-amber-600 mt-1">
                This suburb has no sales to generate aggregated market statistics.
              </p>
            </Card>
          ) : (
            <Card className="p-4 border-red-200 bg-red-50">
              <p className="text-sm text-red-600 font-medium">Failed to load data</p>
              <p className="text-xs text-red-500 mt-1">{error}</p>
            </Card>
          ))}

        {/* No insights data (SP returned rows but all zeros) */}
        {!loading && !error && !metricRecord && selectedSuburb && (
          <Card className="p-4 border-amber-200 bg-amber-50">
            {sales.length > 0 ? (
              <>
                <p className="text-sm text-amber-700 font-medium">
                  No aggregated insights available
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Aggregated insights are not published for this suburb and period. REINZ requires a
                  minimum of 5 sales to produce reliable statistics. Individual sales recorded after
                  the statistics generation may be visible below.
                </p>
              </>
            ) : (
              <p className="text-sm text-amber-700 font-medium">No aggregated insights available</p>
            )}
          </Card>
        )}

        {/* Data */}
        {!loading && !error && metricRecord && selectedSuburb && (
          <>
            <Card className="p-4 relative">
              <button
                onClick={handleShare}
                disabled={isSharing}
                data-share-btn
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50"
                title="Share"
              >
                {isSharing ? (
                  <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Share2 className="w-4 h-4 text-primary" />
                )}
              </button>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-primary self-center" />
                <h3 className="text-lg font-semibold leading-tight">{selectedSuburb.suburbName}</h3>
                <span className="text-sm text-muted-foreground">{selectedSuburb.taName}</span>
              </div>
              <p className="text-2xl font-bold">{formatPrice(metricRecord.MedianSalePrice)}</p>
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <MetricCard label="Monthly Sales" value={metricRecord.NoOfSales.toLocaleString()} />
              <MetricCard label="Days to Sell" value={`${metricRecord.DaysToSell} days`} />
              <MetricCard
                label="Median Valuation"
                value={formatPrice(metricRecord.MedianValuation)}
              />
              <MetricCard
                label="Sale vs Valuation"
                value={`${(metricRecord.PercSaleToValDiff * 100).toFixed(0)}%`}
              />
            </div>

            <PriceTrendChart
              records={records}
              chartRange={chartRange}
              onRangeChange={setChartRange}
              dateTo={dateTo}
            />

            <Card className="p-4 bg-blue-50 border-l-4 border-l-blue-500">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2">
                Market Summary · {currentMonthLabel}
              </p>
              <p className="text-sm text-blue-900 leading-relaxed">
                In <strong>{currentMonthLabel}</strong>, {selectedSuburb.suburbName}'s median sale
                price was <strong>{formatPrice(metricRecord.MedianSalePrice)}</strong>, with homes
                selling in <strong>{metricRecord.DaysToSell} days</strong>.
                {metricRecord.PercSalePriceDiffLastPeriod != null && (
                  <>
                    {' '}
                    Sales prices are{' '}
                    <strong
                      className={
                        metricRecord.PercSalePriceDiffLastPeriod >= 0
                          ? 'text-emerald-600'
                          : 'text-red-500'
                      }
                    >
                      {metricRecord.PercSalePriceDiffLastPeriod === 0
                        ? 'unchanged'
                        : metricRecord.PercSalePriceDiffLastPeriod > 0
                          ? 'up'
                          : 'down'}{' '}
                      {metricRecord.PercSalePriceDiffLastPeriod !== 0
                        ? `${Math.abs(metricRecord.PercSalePriceDiffLastPeriod)}%`
                        : ''}
                    </strong>{' '}
                    compared to last period.
                  </>
                )}
              </p>
            </Card>
          </>
        )}
      </div>
      {/* end contentRef */}

      {/* Individual Sales Section — outside contentRef (not exported to PDF) */}
      {selectedSuburb && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Sales in {salesMonthLabel}</h3>
            {salesLoading ? (
              <span className="text-xs text-muted-foreground">Loading…</span>
            ) : (
              <span className="text-xs font-medium text-primary">
                {filteredSales.length} of {sales.length} sales
              </span>
            )}
          </div>

          {/* Filter row */}
          {!salesLoading && sales.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {/* Price range slider */}
              <div className="col-span-2 space-y-1.5">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatPrice(priceRange[0])}</span>
                  <span className="font-medium text-foreground">Price range</span>
                  <span>{formatPrice(priceRange[1])}</span>
                </div>
                <Slider
                  min={0}
                  max={sliderMax}
                  step={50000}
                  value={priceRange}
                  onValueChange={(v) => setPriceRange(v as [number, number])}
                />
              </div>

              {/* Bedrooms */}
              <select
                value={filterBedrooms}
                onChange={(e) => setFilterBedrooms(e.target.value)}
                className="border rounded px-2 py-1 text-xs bg-background text-foreground"
              >
                <option value="any">Any bedrooms</option>
                {['1', '2', '3+', '4+', '5+'].map((v) => (
                  <option key={v} value={v}>
                    {v} bed{v === '1' ? '' : 's'}
                  </option>
                ))}
              </select>

              {/* Bathrooms */}
              <select
                value={filterBathrooms}
                onChange={(e) => setFilterBathrooms(e.target.value)}
                className="border rounded px-2 py-1 text-xs bg-background text-foreground"
              >
                <option value="any">Any bathrooms</option>
                {['1', '2', '3+'].map((v) => (
                  <option key={v} value={v}>
                    {v} bath{v === '1' ? '' : 's'}
                  </option>
                ))}
              </select>

              {/* Category */}
              {categories.length > 0 && (
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="border rounded px-2 py-1 text-xs bg-background text-foreground"
                >
                  <option value="any">Any category</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              )}

              {/* Sale Method */}
              {saleMethods.length > 0 && (
                <select
                  value={filterSaleMethod}
                  onChange={(e) => setFilterSaleMethod(e.target.value)}
                  className="border rounded px-2 py-1 text-xs bg-background text-foreground"
                >
                  <option value="any">Any method</option>
                  {saleMethods.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Individual sale rows */}
          {!salesLoading && filteredSales.length > 0 && (
            <div className="space-y-2">
              {filteredSales.map((s) => (
                <Card key={s.saleID} className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground">
                        {s.salePrice
                          ? formatPrice(s.salePrice)
                          : (s.salePriceRange ?? 'Price withheld')}
                      </p>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        {s.bedrooms != null && (
                          <span className="text-xs text-muted-foreground">{s.bedrooms} bed</span>
                        )}
                        {s.bathrooms != null && (
                          <span className="text-xs text-muted-foreground">{s.bathrooms} bath</span>
                        )}
                        {s.floorArea != null && s.floorArea > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {s.floorArea}m² floor
                          </span>
                        )}
                        {s.landArea != null && s.landArea > 0 && (
                          <span className="text-xs text-muted-foreground">{s.landArea}m² land</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-muted-foreground">
                        {new Date(s.saleDate).toLocaleDateString('en-NZ', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                      {s.sellDays != null && s.sellDays > 0 && (
                        <p className="text-xs text-muted-foreground">{s.sellDays} days</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {s.saleMethod && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {s.saleMethod}
                      </span>
                    )}
                    {s.category && (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        {s.category}
                      </span>
                    )}
                    {s.freeholdStatus && s.freeholdStatus !== 'Unknown' && (
                      <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        {s.freeholdStatus}
                      </span>
                    )}
                    {s.isNewDwelling && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                        New dwelling
                      </span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {!salesLoading && sales.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-3">
              No sales data found for this month.
            </p>
          )}

          {!salesLoading && sales.length > 0 && filteredSales.length === 0 && (
            <div className="text-center py-3 space-y-2">
              <p className="text-xs text-muted-foreground">No sales match the selected filters.</p>
              <button
                onClick={() => {
                  setPriceRange([0, sliderMax])
                  setFilterBedrooms('any')
                  setFilterBathrooms('any')
                  setFilterCategory('any')
                  setFilterSaleMethod('any')
                }}
                className="text-xs text-primary underline"
              >
                Reset filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// MULTI-SUBURB TREND CHART
// ─────────────────────────────────────────────────────────────
function MultiSuburbChart({
  suburbs,
  suburbData,
  chartRange,
  onRangeChange,
  dateTo,
  hideRangeButtons,
  customRange,
  onCustomRangeChange,
  compact,
}: {
  suburbs: SuburbLookup[]
  suburbData: Record<number, MarketInsightRecord[]>
  chartRange?: ChartRange
  onRangeChange?: (r: ChartRange) => void
  dateTo: Date
  hideRangeButtons?: boolean
  customRange?: CustomRange
  onCustomRangeChange?: (r: CustomRange) => void
  compact?: boolean
}) {
  const allDates = [
    ...new Set(suburbs.flatMap((s) => (suburbData[s.suburbID] ?? []).map((r) => r.Date))),
  ].sort()

  if (allDates.length < 2) return null

  // Compute domain first so chart data can be clamped within it
  const allPrices = suburbs.flatMap((s) =>
    (suburbData[s.suburbID] ?? [])
      .filter((r) => r.MedianSalePrice >= 50000 && r.NoOfSales >= 5)
      .map((r) => r.MedianSalePrice),
  )
  const sorted = [...allPrices].sort((a, b) => a - b)
  const p10 = sorted[Math.max(0, Math.floor(sorted.length * 0.1))] ?? 0
  const p90 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.9))] ?? 1_000_000
  const yDomain: [number, number] = [
    Math.floor((p10 * 0.85) / 50000) * 50000,
    Math.ceil((p90 * 1.2) / 50000) * 50000,
  ]

  // Clamp values to domain — html2canvas ignores SVG clip-paths so lines would escape if unclamped
  const chartData = allDates.map((date) => {
    const point: Record<string, number | string> = { date: formatChartDate(date) }
    suburbs.forEach((s) => {
      const record = (suburbData[s.suburbID] ?? []).find((r) => r.Date === date)
      if (record && record.MedianSalePrice >= 50000 && record.NoOfSales >= 5) {
        point[`s_${s.suburbID}`] = Math.max(
          yDomain[0],
          Math.min(yDomain[1], record.MedianSalePrice),
        )
      }
    })
    return point
  })

  const availableMonths = getAvailableMonths(dateTo)

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-muted-foreground">Median Sale Price</p>
        {!hideRangeButtons && customRange !== undefined ? (
          <div className="flex rounded-lg border overflow-hidden">
            {CUSTOM_RANGES.map((r) => (
              <button
                key={r}
                onClick={() => onCustomRangeChange?.(r)}
                data-range-btn={r}
                data-range-active={String(customRange === r)}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${customRange === r ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`}
              >
                {r}
              </button>
            ))}
          </div>
        ) : !hideRangeButtons ? (
          <div className="flex gap-1">
            {RANGE_LABELS.map((r) => {
              const disabled = RANGE_MONTHS[r] > availableMonths
              return (
                <button
                  key={r}
                  onClick={() => {
                    if (!disabled) onRangeChange?.(r)
                  }}
                  disabled={disabled}
                  className={`w-8 py-1 text-xs rounded font-medium text-center transition-colors ${
                    disabled
                      ? 'text-muted-foreground/30 cursor-not-allowed'
                      : chartRange === r
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {r}
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-3 mb-3">
        {suburbs.map((s, i) => (
          <div key={s.suburbID} className="flex items-center gap-1.5">
            <div
              className="w-3 h-2 rounded-sm"
              style={{ backgroundColor: SUBURB_COLORS[i % SUBURB_COLORS.length] }}
            />
            <span className="text-xs text-muted-foreground">{s.suburbName}</span>
          </div>
        ))}
      </div>
      {/* overflow-hidden + [&_svg]:overflow-hidden clips lines at CSS level — html2canvas ignores SVG clipPath */}
      <div className={compact ? 'overflow-hidden [&_svg]:overflow-hidden' : undefined}>
        <ResponsiveContainer width="100%" height={compact ? 160 : 200}>
          <LineChart data={chartData} margin={{ top: 4, right: 24, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: compact ? 9 : 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              interval={customRange === '1Y' ? 2 : customRange === '6M' ? 1 : 0}
            />
            <YAxis
              tickFormatter={(v) =>
                v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${(v / 1_000).toFixed(0)}K`
              }
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              width={52}
              domain={yDomain}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                const id = parseInt(name.replace('s_', ''))
                const suburb = suburbs.find((s) => s.suburbID === id)
                const formatted =
                  value >= 1_000_000
                    ? `$${(value / 1_000_000).toFixed(2)}M`
                    : `$${(value / 1_000).toFixed(0)}K`
                return [formatted, suburb?.suburbName ?? name]
              }}
              labelStyle={{ fontSize: 11, fontWeight: 600 }}
              contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0' }}
            />
            {suburbs.map((s, i) => (
              <Line
                key={s.suburbID}
                type="linear"
                dataKey={`s_${s.suburbID}`}
                stroke={SUBURB_COLORS[i % SUBURB_COLORS.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────
// CUSTOM STATS VIEW
// ─────────────────────────────────────────────────────────────
const PREF_KEY = 'custom_stats_selection'

function CustomStatsView({ latestMonth, partyId }: { latestMonth: Date; partyId: string }) {
  const [selectedSuburbs, setSelectedSuburbs] = useState<SuburbLookup[]>([])
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SuburbLookup[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const [dateTo, setDateTo] = useState<Date>(latestMonth)
  const [customRange, setCustomRange] = useState<CustomRange>('3M')

  const [suburbData, setSuburbData] = useState<Record<number, MarketInsightRecord[]>>({})
  const [loading, setLoading] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const prefLoaded = useRef(false)

  useEffect(() => {
    setDateTo((prev) => (prev > latestMonth ? latestMonth : prev))
  }, [latestMonth.getTime()]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowDropdown(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (query.length < 2) {
      setSearchResults([])
      setShowDropdown(false)
      return
    }
    const t = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const results = await fetchSuburbSuggestions(query)
        setSearchResults(results.slice(0, 8))
        setShowDropdown(true)
      } catch {
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [query])

  // Load saved preference on mount and auto-fetch if found
  useEffect(() => {
    if (!partyId) {
      prefLoaded.current = true
      return
    }
    const load = async () => {
      try {
        const res = await fetch(`api/user-preferences/${PREF_KEY}?userId=${partyId}`)
        if (!res.ok) {
          prefLoaded.current = true
          return
        }
        const pref = await res.json()
        if (!pref?.value) {
          prefLoaded.current = true
          return
        }
        const saved = JSON.parse(pref.value) as {
          suburbs: SuburbLookup[]
          customRange?: CustomRange
        }
        if (!saved.suburbs?.length) {
          prefLoaded.current = true
          return
        }
        setSelectedSuburbs(saved.suburbs)
        sessionStorage.setItem('custom_stats_suburbs', JSON.stringify(saved.suburbs))
        const range = saved.customRange ?? '3M'
        setCustomRange(range)
        prefLoaded.current = true
        fetchInsights(saved.suburbs, range, latestMonth)
      } catch {
        prefLoaded.current = true
      }
    }
    load()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const savePreference = async (suburbs: SuburbLookup[], range: CustomRange) => {
    if (!partyId) return
    sessionStorage.setItem('custom_stats_suburbs', JSON.stringify(suburbs))
    try {
      await fetch('api/user-preferences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          UserId: partyId,
          PreferenceKey: PREF_KEY,
          PreferenceValue: JSON.stringify({ suburbs, customRange: range }),
        }),
      })
      window.dispatchEvent(new CustomEvent('custom-stats-updated'))
    } catch {
      // silently fail
    }
  }

  const MAX_SUBURBS = 8

  const addSuburb = (suburb: SuburbLookup) => {
    if (selectedSuburbs.length >= MAX_SUBURBS) return
    if (selectedSuburbs.some((s) => s.suburbID === suburb.suburbID)) return
    const next = [...selectedSuburbs, suburb]
    setSelectedSuburbs(next)
    setQuery('')
    setSearchResults([])
    setShowDropdown(false)
    if (prefLoaded.current) savePreference(next, customRange)
    if (hasFetched) fetchInsights(next, customRange, dateTo)
  }

  const removeSuburb = (suburbID: number) => {
    const next = selectedSuburbs.filter((s) => s.suburbID !== suburbID)
    setSelectedSuburbs(next)
    setSuburbData((prev) => {
      const d = { ...prev }
      delete d[suburbID]
      return d
    })
    if (prefLoaded.current) savePreference(next, customRange)
  }

  const fetchInsights = async (suburbs: SuburbLookup[], range: CustomRange, to: Date) => {
    if (suburbs.length === 0) return
    setLoading(true)
    setHasFetched(true)
    try {
      const dateFrom = clampDateFrom(getCustomDateFrom(range, to))
      const results = await Promise.all(
        suburbs.map(async (suburb) => {
          const params = new URLSearchParams({
            GroupingType: '5',
            Grouping1: String(suburb.suburbID),
            Grouping2: 'all',
            Frequency: '1',
            DateFrom: toApiDateString(dateFrom),
            DateTo: toApiDateString(new Date(to.getFullYear(), to.getMonth() + 1, 1)),
          })
          const res = await fetch(`api/report/market-insights?${params}`)
          if (!res.ok) return { suburbID: suburb.suburbID, records: [] as MarketInsightRecord[] }
          const records: MarketInsightRecord[] = await res.json()
          return {
            suburbID: suburb.suburbID,
            records: (records ?? []).sort(
              (a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime(),
            ),
          }
        }),
      )
      const dataMap: Record<number, MarketInsightRecord[]> = {}
      results.forEach((r) => {
        dataMap[r.suburbID] = r.records
      })
      setSuburbData(dataMap)
    } finally {
      setLoading(false)
    }
  }

  const handleGetInsights = async () => {
    await fetchInsights(selectedSuburbs, customRange, dateTo)
    savePreference(selectedSuburbs, customRange)
    trackEvent('custom_stats_get_insights', { suburb_count: selectedSuburbs.length })
  }

  const handleRangeChange = (range: CustomRange) => {
    setCustomRange(range)
    if (hasFetched && selectedSuburbs.length > 0) fetchInsights(selectedSuburbs, range, dateTo)
  }

  const getRecordForMonth = (
    records: MarketInsightRecord[],
    month: Date,
  ): MarketInsightRecord | null => {
    const match = records.find((r) => {
      const d = new Date(r.Date)
      return d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth()
    })
    if (!match || (match.MedianSalePrice ?? 0) === 0) return null
    return match
  }

  const currentMonthLabel = `${MONTH_NAMES[dateTo.getMonth()]} ${dateTo.getFullYear()}`

  // Compute market summary for the results section
  const summaryRecords = selectedSuburbs
    .map((s) => ({ suburb: s, record: getRecordForMonth(suburbData[s.suburbID] ?? [], dateTo) }))
    .filter((x): x is { suburb: SuburbLookup; record: MarketInsightRecord } => x.record !== null)
  const sortedByPrice = [...summaryRecords].sort(
    (a, b) => b.record.MedianSalePrice - a.record.MedianSalePrice,
  )
  const summaryHighest = sortedByPrice[0] ?? null
  const summaryLowest = sortedByPrice.length > 1 ? sortedByPrice[sortedByPrice.length - 1] : null
  const summaryBestChange =
    [...summaryRecords]
      .filter((x) => x.record.PercSalePriceDiffLastPeriod != null)
      .sort(
        (a, b) =>
          Math.abs(b.record.PercSalePriceDiffLastPeriod ?? 0) -
          Math.abs(a.record.PercSalePriceDiffLastPeriod ?? 0),
      )[0] ?? null
  const summaryDays = summaryRecords.filter((x) => x.record.DaysToSell > 0)
  const summaryAvgDays = summaryDays.length
    ? Math.round(summaryDays.reduce((s, x) => s + x.record.DaysToSell, 0) / summaryDays.length)
    : null
  const summaryParts: string[] = []
  if (summaryHighest) {
    summaryParts.push(
      summaryRecords.length > 1
        ? `${summaryHighest.suburb.suburbName} had the highest median at ${formatPrice(summaryHighest.record.MedianSalePrice)}, ${summaryLowest!.suburb.suburbName} the lowest at ${formatPrice(summaryLowest!.record.MedianSalePrice)}.`
        : `${summaryHighest.suburb.suburbName}'s median sale price was ${formatPrice(summaryHighest.record.MedianSalePrice)}.`,
    )
  }
  if (
    summaryBestChange?.record.PercSalePriceDiffLastPeriod != null &&
    summaryBestChange.record.PercSalePriceDiffLastPeriod !== 0
  ) {
    const pct = summaryBestChange.record.PercSalePriceDiffLastPeriod
    summaryParts.push(
      `${summaryBestChange.suburb.suburbName} ${pct > 0 ? 'grew the most at +' : 'declined the most at '}${Math.abs(pct)}% vs the prior period.`,
    )
  }
  if (summaryAvgDays) summaryParts.push(`Average days to sell: ${summaryAvgDays}.`)
  const summaryText = summaryParts.join(' ')

  return (
    <div className="space-y-4">
      {/* Suburb search */}
      <div ref={searchRef} className="space-y-2">
        {selectedSuburbs.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            {selectedSuburbs.map((s, i) => (
              <span
                key={s.suburbID}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full text-white font-medium"
                style={{ backgroundColor: SUBURB_COLORS[i % SUBURB_COLORS.length] }}
              >
                {s.suburbName}
                <button
                  onClick={() => removeSuburb(s.suburbID)}
                  className="ml-0.5 hover:opacity-70 font-bold leading-none"
                >
                  ×
                </button>
              </span>
            ))}
            <button
              onClick={() => {
                setSelectedSuburbs([])
                setSuburbData({})
                setHasFetched(false)
                if (partyId) savePreference([], customRange)
                window.dispatchEvent(new CustomEvent('custom-stats-cleared'))
              }}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors ml-auto"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="relative">
          <div
            className={`flex items-center border rounded-lg px-3 py-2 bg-background gap-2 focus-within:ring-1 focus-within:ring-primary ${selectedSuburbs.length >= MAX_SUBURBS ? 'opacity-50' : ''}`}
          >
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                if (searchResults.length > 0) setShowDropdown(true)
              }}
              disabled={selectedSuburbs.length >= MAX_SUBURBS}
              placeholder={
                selectedSuburbs.length >= MAX_SUBURBS
                  ? `Maximum ${MAX_SUBURBS} suburbs reached`
                  : selectedSuburbs.length === 0
                    ? 'Search and add suburbs...'
                    : 'Add another suburb...'
              }
              className="flex-1 bg-transparent text-sm outline-hidden disabled:cursor-not-allowed"
            />
            {searchLoading && (
              <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
            )}
          </div>
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-52 overflow-y-auto">
              {searchResults
                .filter((r) => !selectedSuburbs.some((s) => s.suburbID === r.suburbID))
                .map((s) => (
                  <button
                    key={s.suburbID}
                    onMouseDown={() => addSuburb(s)}
                    className="w-full text-left px-3 py-2.5 hover:bg-muted transition-colors"
                  >
                    <p className="text-sm font-medium">{s.suburbName}</p>
                    <p className="text-xs text-muted-foreground">{s.taName}</p>
                  </button>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <MonthPicker value={dateTo} latestMonth={latestMonth} onChange={setDateTo} />
        {!hasFetched && (
          <button
            onClick={handleGetInsights}
            disabled={selectedSuburbs.length === 0 || loading}
            className="ml-auto px-4 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg disabled:opacity-50 transition-opacity"
          >
            {loading ? 'Loading…' : 'Get Insights'}
          </button>
        )}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div className="grid grid-cols-2 gap-3 animate-pulse">
          {[...Array(Math.min(selectedSuburbs.length || 2, 4))].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-lg" />
          ))}
        </div>
      )}

      {/* Results */}
      {!loading && hasFetched && selectedSuburbs.length > 0 && (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-semibold text-foreground">{currentMonthLabel}</p>
            <p className="text-xs text-muted-foreground">
              {customRange} period · {selectedSuburbs.length} suburb
              {selectedSuburbs.length !== 1 ? 's' : ''} compared
            </p>
          </div>
          {summaryText && (
            <Card className="p-4 bg-blue-50 border-l-4 border-l-blue-500">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">
                Market Summary · {currentMonthLabel}
              </p>
              <p className="text-sm text-blue-900 leading-snug">
                In <strong>{currentMonthLabel}</strong>, {summaryText}
              </p>
            </Card>
          )}
          <MultiSuburbChart
            suburbs={selectedSuburbs}
            suburbData={suburbData}
            dateTo={dateTo}
            customRange={customRange}
            onCustomRangeChange={handleRangeChange}
          />
          {selectedSuburbs.length <= 4 ? (
            <div className="grid grid-cols-2 gap-3">
              {selectedSuburbs.map((suburb, i) => {
                const records = suburbData[suburb.suburbID] ?? []
                const record = getRecordForMonth(records, dateTo)
                return (
                  <Card key={suburb.suburbID} className="p-3 space-y-2">
                    <div className="flex items-start gap-1.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full shrink-0 mt-0.5"
                        style={{ backgroundColor: SUBURB_COLORS[i % SUBURB_COLORS.length] }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold leading-tight">{suburb.suburbName}</p>
                        <p className="text-xs text-muted-foreground">{suburb.taName}</p>
                      </div>
                    </div>
                    {record ? (
                      <>
                        <p className="text-xl font-bold">{formatPrice(record.MedianSalePrice)}</p>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Days to sell</span>
                            <span className="font-medium">
                              {record.DaysToSell > 0 ? record.DaysToSell : '—'}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Sales</span>
                            <span className="font-medium">{record.NoOfSales}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">vs prior</span>
                            {record.PercSalePriceDiffLastPeriod != null &&
                            record.PercSalePriceDiffLastPeriod !== 0 ? (
                              <span
                                className={`font-medium ${record.PercSalePriceDiffLastPeriod > 0 ? 'text-emerald-600' : 'text-red-500'}`}
                              >
                                {record.PercSalePriceDiffLastPeriod > 0 ? '+' : ''}
                                {record.PercSalePriceDiffLastPeriod}%
                              </span>
                            ) : (
                              <span className="font-medium text-muted-foreground">—</span>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-xs text-amber-700 font-medium py-1">
                        No aggregated insights available
                      </p>
                    )}
                  </Card>
                )
              })}
            </div>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                      Suburb
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">
                      Price
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">
                      Days
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">
                      Sales
                    </th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">
                      vs Prior
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSuburbs.map((suburb, i) => {
                    const records = suburbData[suburb.suburbID] ?? []
                    const record = getRecordForMonth(records, dateTo)
                    return (
                      <tr key={suburb.suburbID} className="border-b last:border-0">
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <div
                              className="w-2 h-2 rounded-full shrink-0"
                              style={{ backgroundColor: SUBURB_COLORS[i % SUBURB_COLORS.length] }}
                            />
                            <div>
                              <p className="font-medium leading-tight text-xs">
                                {suburb.suburbName}
                              </p>
                              <p className="text-xs text-muted-foreground">{suburb.taName}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold text-xs">
                          {record ? formatPrice(record.MedianSalePrice) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs">
                          {record && record.DaysToSell > 0 ? record.DaysToSell : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs">
                          {record ? record.NoOfSales : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs">
                          {record?.PercSalePriceDiffLastPeriod != null &&
                          record.PercSalePriceDiffLastPeriod !== 0 ? (
                            <span
                              className={
                                record.PercSalePriceDiffLastPeriod > 0
                                  ? 'text-emerald-600'
                                  : 'text-red-500'
                              }
                            >
                              {record.PercSalePriceDiffLastPeriod > 0 ? '+' : ''}
                              {record.PercSalePriceDiffLastPeriod}%
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* Empty state */}
      {!hasFetched && (
        <div className="text-center py-10 text-muted-foreground">
          <SlidersHorizontal className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Search and select suburbs to compare</p>
        </div>
      )}
    </div>
  )
}

export function MarketInsightsScreen() {
  const banner = useBanner('insights')
  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'market_insights' })
  }, [])
  const { accounts } = useMsal()
  const partyId = accounts[0]?.localAccountId ?? ''
  const [latestMonth, setLatestMonth] = useState<Date>(getDefaultLatestMonth())
  const [latestMonthReady, setLatestMonthReady] = useState(false)
  const [activeTab, setActiveTab] = useState(() => {
    const tab = sessionStorage.getItem('insights_initial_tab')
    if (tab) {
      sessionStorage.removeItem('insights_initial_tab')
      return tab
    }
    return 'suburb'
  })

  useEffect(() => {
    fetchLatestAvailableMonth()
      .then((d) => {
        setLatestMonth(d)
        setLatestMonthReady(true)
      })
      .catch(() => {
        setLatestMonthReady(true)
      }) // use safe default on error
  }, [])

  return (
    <div className="pb-4">
      <ServiceBanner banner={banner} />
      <Tabs
        value={activeTab}
        onValueChange={(tab) => {
          setActiveTab(tab)
          trackEvent('market_tab_switched', { tab })
        }}
      >
        <TabsList className="w-full grid grid-cols-4 mb-6">
          <TabsTrigger value="national">National</TabsTrigger>
          <TabsTrigger value="regional">Regional</TabsTrigger>
          <TabsTrigger value="suburb">Suburb</TabsTrigger>
          <TabsTrigger value="custom">Multi-Suburb</TabsTrigger>
        </TabsList>

        {!latestMonthReady && (
          <div className="grid grid-cols-2 gap-3 animate-pulse mt-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded-lg" />
            ))}
          </div>
        )}

        <TabsContent value="national">
          {latestMonthReady && <NationalView partyId={partyId} latestMonth={latestMonth} />}
        </TabsContent>

        <TabsContent value="regional">
          {latestMonthReady && <RegionalView partyId={partyId} latestMonth={latestMonth} />}
        </TabsContent>

        <TabsContent value="suburb">
          {latestMonthReady && <SuburbView partyId={partyId} latestMonth={latestMonth} />}
        </TabsContent>

        <TabsContent value="custom">
          {latestMonthReady && <CustomStatsView latestMonth={latestMonth} partyId={partyId} />}
        </TabsContent>
      </Tabs>
      <PoweredByFooter />
    </div>
  )
}
